import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { SERVER_URL } from "../config";
import { XMarkIcon, ArrowDownTrayIcon, EyeIcon } from "@heroicons/react/24/outline";

/* ─── hardcoded logo paths ─── */
const LOGO1_PATH = "/sbefintech_logo.png";
const LOGO2_PATH = "/tally.jpg";

/* helper: load image path → base64 for jsPDF */
const imgToBase64 = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

/* ─── tiny helpers ─── */
const loadScript = (src) =>
  new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement("script");
    s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0 });

/* ─── shared style tokens ─── */
const INPUT_CLASS =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors";

const INPUT_READONLY =
  "w-full bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-400 cursor-not-allowed select-none";

const FieldLabel = ({ children }) => (
  <label className="text-xs text-slate-500 font-medium">{children}</label>
);

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-2 mb-3 mt-1">
    <div
      className="w-1 h-4 rounded-full flex-shrink-0"
      style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
    />
    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 m-0">
      {children}
    </p>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   PDF generator
══════════════════════════════════════════════════════════════════════ */
const buildPDF = async ({
  selectedCustomer,
  rows,
  grandTotal,
  subject,
  employeeName,
  employeePhone,
  forEmail = false,
}) => {
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");

  /* load logos from local paths */
  const [logo1, logo2] = await Promise.all([
    imgToBase64(LOGO1_PATH),
    imgToBase64(LOGO2_PATH),
  ]);

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, ml = 15, mr = W - 15;

  /* ── dark header bar ── */
  doc.setFillColor(30, 18, 60);
  doc.rect(0, 0, W, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("SBE Fintech Software Pvt. Ltd.", ml, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("Registered Office: 203/C, BB Ganguly Street, Kolkata – 700012", ml, 18);
  doc.text("CIN: U62091WB2023PTC266642", ml, 23);

  /* ── logo images (top-right corner) ── */
  const imgW = 21, imgH = 18, gap = 3, imgY = 5;
  if (logo2) {
    try { doc.addImage(logo2, "PNG", mr - imgW, imgY, imgW, imgH); } catch (_) {}
  }
  if (logo1) {
    const x2 = logo2 ? mr - imgW - gap - imgW : mr - imgW;
    try { doc.addImage(logo1, "PNG", x2, imgY, imgW, imgH); } catch (_) {}
  }

  let y = 35;

  /* ── date ── */
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0);
  doc.text(
    `Date: ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" })}`,
    mr, y, { align: "right" },
  );
  y += 8;

  /* ── To block ── */
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.text("To.", ml, y); y += 6;
  doc.setFontSize(10);
  doc.text(selectedCustomer?.companyName || "—", ml, y); y += 5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(selectedCustomer?.address || "", ml, y); y += 5;
  doc.text(`Kolkata: ${selectedCustomer?.pincode || ""}`, ml, y); y += 10;

  /* ── Subject (dynamic) ── */
  const subLine = `Sub: ${subject || "—"}`;
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.text(subLine, ml, y); y += 2;
  doc.setLineWidth(0.3); doc.setDrawColor(30, 18, 60);
  const lineEnd = Math.min(ml + doc.getTextWidth(subLine) + 4, mr);
  doc.line(ml, y, lineEnd, y); y += 6;

  doc.setFont("helvetica", "normal"); doc.text("Dear Sir,", ml, y); y += 5;
  const bodyText =
    "SBE Fintech Software is pleased to submit the price proposal for the below items. " +
    "The pricing is quoted in INR only with a validity of 7 days from the date of offer.";
  const bodyLines = doc.splitTextToSize(bodyText, mr - ml);
  doc.setFontSize(9);
  doc.text(bodyLines, ml, y);
  y += bodyLines.length * 5 + 4;

  /* ── items table ── */
  doc.autoTable({
    startY: y,
    head: [["SKU", "Item", "Total in INR", "Inclusive Tax"]],
    body: [
      ...rows.map((r) => [r.sku || "—", r.item || "—", fmt(r.base), fmt(r.inclTax)]),
      ["", { content: "Total", styles: { fontStyle: "bold" } }, "", { content: fmt(grandTotal), styles: { fontStyle: "bold" } }],
    ],
    theme: "grid",
    headStyles: { fillColor: [30, 18, 60], textColor: 255, fontSize: 9, halign: "center" },
    bodyStyles: { fontSize: 9, textColor: [0, 0, 0] },
    columnStyles: {
      0: { cellWidth: 38 }, 1: { cellWidth: 68 },
      2: { halign: "center", cellWidth: 38 }, 3: { halign: "center", cellWidth: 38 },
    },
    margin: { left: ml, right: 15 },
    styles: { lineColor: [180, 180, 200], lineWidth: 0.3 },
  });
  y = doc.lastAutoTable.finalY + 10;

  /* ── terms ── */
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(0);
  doc.text("Terms and Condition:", ml, y); y += 2;
  doc.line(ml, y, ml + 58, y); y += 5;
  const validDate = new Date(Date.now() + 7 * 864e5)
    .toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  const terms = [
    "GST @ 18% will be applicable on all the prices mentioned above.",
    "100% payment must be made in favour of SBE Fintech Software against the invoice raised.",
    `This proposal is valid up to ${validDate}.`,
  ];
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5);
  terms.forEach((t) => {
    const tl = doc.splitTextToSize(`\u2022  ${t}`, mr - ml);
    doc.text(tl, ml, y);
    y += tl.length * 4.5 + 1.5;
  });
  y += 4;

  /* ── bank details ── */
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.text("Please Find The Account Details Below-", ml, y); y += 2;
  doc.line(ml, y, ml + 80, y); y += 5;
  const bankRows = [
    ["Account Number:", "50200087662987"],
    ["IFSC:",           "HDFC0000008"],
    ["Bank Name:",      "HDFC"],
    ["Branch:",         "Stephen House"],
    ["Account Type:",   "Current"],
    ["From:",           "SBE Fintech Software Pvt Ltd."],
  ];
  doc.setFontSize(8.5);
  bankRows.forEach(([k, v]) => {
    doc.setFont("helvetica", "bold"); doc.text(k, ml, y);
    doc.setFont("helvetica", "normal"); doc.text(v, ml + 38, y);
    y += 5;
  });
  y += 6;

  /* ── employee sign-off (dynamic) ── */
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.text(employeeName || "—", ml, y); y += 5;
  doc.setFont("helvetica", "normal");
  if (employeePhone) doc.text(employeePhone, ml, y);

  /* ── footer ── */
  const pH = doc.internal.pageSize.height;
  doc.setDrawColor(139, 92, 246); doc.setLineWidth(0.5);
  doc.line(ml, pH - 22, mr, pH - 22);
  doc.setFontSize(7); doc.setTextColor(80); doc.setFont("helvetica", "normal");
  [
    "Registered Office: 203/C, BB Ganguly Street, Kolkata – 700012 | CIN: U62091WB2023PTC266642",
    "Branch: Ranchi / Kolkata / Dhaka | Mail: info@sbefintech.com | Toll Free: 1800 889 1973 | www.sbefintech.com",
  ].forEach((fl, i) => doc.text(fl, W / 2, pH - 16 + i * 4.5, { align: "center" }));

  if (forEmail) return doc.output("datauristring");
  doc.save(`Quotation_${(selectedCustomer?.companyName || "Client").replace(/\s+/g, "_")}_${Date.now()}.pdf`);
  return null;
};

/* ══════════════════════════════════════════════════════════════════════
   Main component
══════════════════════════════════════════════════════════════════════ */
export default function QuotationModal({ isOpen, onClose }) {
  /* ── tab: "lead" | "outside" ── */
  const [activeTab, setActiveTab] = useState("lead");

  /* ── customer search (From Lead) ── */
  const [customerQuery, setCustomerQuery]       = useState("");
  const [customers, setCustomers]               = useState([]);
  const [filteredCust, setFilteredCust]         = useState([]);
  const [showDropdown, setShowDropdown]         = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const dropRef = useRef(null);

  /* ── Outside Lead manual fields ── */
  const [outsideName,    setOutsideName]    = useState("");
  const [outsideAddress, setOutsideAddress] = useState("");
  const [outsideEmail,   setOutsideEmail]   = useState("");

  /* ── quotation meta ── */
  const [subject,       setSubject]       = useState("");
  const [employeePhone, setEmployeePhone] = useState("");

  /* ── items ── */
  const [items, setItems] = useState([
    { sku: "", item: "", total: "", gstPct: 18, tallySlNo: "", status: "" },
  ]);

  /* ── submission / preview ── */
  const [submitting,  setSubmitting]  = useState(false);
  const [submitted,   setSubmitted]   = useState(false);
  const [quotationId, setQuotationId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [submitMsg,   setSubmitMsg]   = useState("");

  /* ── employee from localStorage ── */
  const emp          = JSON.parse(localStorage.getItem("employee") || "{}");
  const employeeName = emp.employee_name || "";

  /* ── effective customer object (used everywhere) ── */
  const effectiveCustomer =
    activeTab === "lead"
      ? selectedCustomer
      : {
          companyName:   outsideName,
          address:       outsideAddress,
          mailId:        outsideEmail,
          contactPerson: "",
          mobileNumber:  "",
          pincode:       "",
        };

  /* ── fetch leads ── */
  useEffect(() => {
    if (!isOpen) return;
    axios
      .post(`${SERVER_URL}/DataFetch/leadHistory.php`, {
        userName: emp.employee_name,
        userId:   emp.employee_id,
      })
      .then((r) => { if (r.data.status === "success") setCustomers(r.data.leads || []); })
      .catch(console.error);
  }, [isOpen]);

  /* ── filter dropdown ── */
  useEffect(() => {
    if (!customerQuery.trim()) { setFilteredCust([]); setShowDropdown(false); return; }
    const q = customerQuery.toLowerCase();
    setFilteredCust(
      customers
        .filter((c) =>
          (c.companyName   || "").toLowerCase().includes(q) ||
          (c.contactPerson || "").toLowerCase().includes(q),
        )
        .slice(0, 8),
    );
    setShowDropdown(true);
  }, [customerQuery, customers]);

  /* ── close dropdown on outside click ── */
  useEffect(() => {
    const h = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* ── reset on close ── */
  const handleClose = () => {
    setActiveTab("lead");
    setCustomerQuery(""); setSelectedCustomer(null);
    setOutsideName(""); setOutsideAddress(""); setOutsideEmail("");
    setSubject(""); setEmployeePhone("");
    setItems([{ sku: "", item: "", total: "", gstPct: 18, tallySlNo: "", status: "" }]);
    setSubmitted(false); setShowPreview(false); setSubmitMsg(""); setQuotationId(null);
    onClose();
  };

  if (!isOpen) return null;

  /* ── item helpers ── */
  const addItem    = () => setItems((p) => [...p, { sku: "", item: "", total: "", gstPct: 18, tallySlNo: "", status: "" }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => setItems((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  /* ── computed rows ── */
  const rows = items.map((r) => {
    const base    = parseFloat(r.total)  || 0;
    const gst     = parseFloat(r.gstPct) || 0;
    const inclTax = base + (base * gst) / 100;
    return { ...r, base, inclTax };
  });
  const grandTotal = rows.reduce((s, r) => s + r.inclTax, 0);

  /* ── validation ── */
  const isValid = () => {
    if (activeTab === "lead" && !selectedCustomer) return false;
    if (activeTab === "outside" && !outsideName.trim()) return false;
    if (!subject.trim()) return false;
    return items.every((r) => r.item.trim() !== "" && r.total !== "" && parseFloat(r.total) > 0);
  };

  /* ── PDF params ── */
  const pdfParams = (forEmail) => ({
    selectedCustomer: effectiveCustomer,
    rows,
    grandTotal,
    subject,
    employeeName,
    employeePhone,
    forEmail,
  });

  /* ── submit to DB ── */
  const handleSubmit = async () => {
    if (!isValid()) {
      setSubmitMsg("Please fill in customer details, a subject, and all item fields.");
      return;
    }
    setSubmitting(true); setSubmitMsg("");
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/insertQuotation.php`, {
        customerName: effectiveCustomer?.companyName,
        subject,
        items: items.map((r) => ({
          sku:             r.sku,
          tallySlNo:       r.tallySlNo,
          itemDescription: r.item,
          amount:          parseFloat(r.total) || 0,
          gst:             parseFloat(r.gstPct) || 0,
          status:          r.status,
        })),
        created_by: employeeName || emp.employee_id,
      });
      if (res.data.status === "success") {
        setSubmitted(true);
        setQuotationId(res.data.quotation_id || null);
        setSubmitMsg("Quotation saved successfully!");
      } else {
        setSubmitMsg(res.data.message || "Failed to save quotation.");
      }
    } catch {
      setSubmitMsg("Server error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── send email ── */
  const handleSendEmail = async () => {
    if (!effectiveCustomer?.mailId) { setSubmitMsg("No email found for selected customer."); return; }
    setSubmitting(true); setSubmitMsg("");
    try {
      const pdfDataUri = await buildPDF(pdfParams(true));
      await axios.post(`${SERVER_URL}/Quotation/sendQuotation.php`, {
        toEmail:    effectiveCustomer.mailId,
        toName:     effectiveCustomer.companyName,
        pdfBase64:  pdfDataUri.split(",")[1],
        senderName: employeeName,
      });
      setSubmitMsg("Quotation emailed successfully!");
    } catch {
      setSubmitMsg("Failed to send email. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════ */
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(8px)" }}
    >
      <div
        className="fixed top-0 left-0 right-0 h-0.5 z-50"
        style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }}
      />

      <div
        className="w-[760px] max-h-[90vh] rounded-xl flex flex-col bg-white border border-slate-200"
        style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div
          className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0 bg-white rounded-t-xl"
          style={{ boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
            <div>
              <h3 className="text-lg font-bold m-0 tracking-tight text-slate-800">Create Quotation</h3>
              <p className="text-xs text-slate-400 m-0 mt-0.5">
                Generates PDF · saves to database · sends via email
              </p>
            </div>
          </div>
          <button type="button" onClick={handleClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* ── Tabs ── */}
        <div className="px-6 pt-4 pb-0 shrink-0 bg-white border-b border-slate-200">
          <div className="flex gap-1">
            {[
              { key: "lead",    label: "From Lead" },
              { key: "outside", label: "Outside Lead" },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                disabled={submitted}
                onClick={() => {
                  setActiveTab(key);
                  /* reset customer selection when switching tabs */
                  setSelectedCustomer(null);
                  setCustomerQuery("");
                  setOutsideName(""); setOutsideAddress(""); setOutsideEmail("");
                }}
                className={`px-5 py-2 text-sm font-semibold rounded-t-lg border-b-2 transition-colors focus:outline-none ${
                  activeTab === key
                    ? "border-blue-600 text-blue-700 bg-blue-50"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div
          className="flex-1 overflow-y-auto px-6 py-5"
          style={{ background: "#f0f4f8", scrollbarWidth: "thin", scrollbarColor: "rgba(148,163,184,0.5) rgba(241,245,249,1)" }}
        >

          {/* ── Customer Details ── */}
          <SectionTitle>Customer Details</SectionTitle>
          <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>

            {activeTab === "lead" ? (
              /* ── FROM LEAD: search dropdown ── */
              <div className="relative" ref={dropRef}>
                <FieldLabel>Search Customer / Company *</FieldLabel>
                <input
                  className={INPUT_CLASS + " mt-1"}
                  placeholder="Type company name or contact person…"
                  value={customerQuery}
                  disabled={submitted}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCustomerQuery(val);
                    if (selectedCustomer && val !== (selectedCustomer.companyName || selectedCustomer.contactPerson || "")) {
                      setSelectedCustomer(null);
                    }
                  }}
                  onFocus={() => customerQuery && setShowDropdown(true)}
                />
                {showDropdown && filteredCust.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                    {filteredCust.map((c, i) => (
                      <div key={i}
                        className="px-4 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                        onClick={() => { setSelectedCustomer(c); setCustomerQuery(c.companyName || c.contactPerson || ""); setShowDropdown(false); }}
                      >
                        <div className="text-sm font-semibold text-slate-700">{c.companyName || "—"}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{c.contactPerson} · {c.mobileNumber} · {c.mailId}</div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedCustomer && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                    <p className="text-sm font-bold text-blue-800 m-0">{selectedCustomer.companyName}</p>
                    <p className="text-xs text-slate-500 mt-1 m-0">{selectedCustomer.contactPerson} · {selectedCustomer.mobileNumber}</p>
                    <p className="text-xs text-slate-500 m-0">
                      {selectedCustomer.address}{selectedCustomer.pincode ? ` – ${selectedCustomer.pincode}` : ""}
                    </p>
                    <p className="text-xs text-blue-500 m-0">{selectedCustomer.mailId}</p>
                  </div>
                )}
              </div>
            ) : (
              /* ── OUTSIDE LEAD: manual fields ── */
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1 sm:col-span-2">
                  <FieldLabel>Customer / Company Name *</FieldLabel>
                  <input
                    className={INPUT_CLASS}
                    placeholder="e.g. Acme Pvt. Ltd."
                    value={outsideName}
                    disabled={submitted}
                    onChange={(e) => setOutsideName(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1 sm:col-span-2">
                  <FieldLabel>Address</FieldLabel>
                  <input
                    className={INPUT_CLASS}
                    placeholder="e.g. 12, Park Street, Kolkata – 700016"
                    value={outsideAddress}
                    disabled={submitted}
                    onChange={(e) => setOutsideAddress(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <FieldLabel>Email</FieldLabel>
                  <input
                    className={INPUT_CLASS}
                    placeholder="e.g. contact@acme.com"
                    type="email"
                    value={outsideEmail}
                    disabled={submitted}
                    onChange={(e) => setOutsideEmail(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── Quotation Details ── */}
          <SectionTitle>Quotation Details</SectionTitle>
          <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              {/* Subject — full width */}
              <div className="flex flex-col gap-1 sm:col-span-2">
                <FieldLabel>Subject *</FieldLabel>
                <input
                  className={INPUT_CLASS}
                  placeholder="e.g. Offer for – Tally Customization."
                  value={subject}
                  disabled={submitted}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>

              {/* Employee name — read-only from localStorage */}
              <div className="flex flex-col gap-1">
                <FieldLabel>Prepared By</FieldLabel>
                <input className={INPUT_READONLY} value={employeeName} readOnly />
              </div>

              {/* Employee phone — editable */}
              <div className="flex flex-col gap-1">
                <FieldLabel>Your Contact Number</FieldLabel>
                <input
                  className={INPUT_CLASS}
                  placeholder="e.g. 98765-43210"
                  value={employeePhone}
                  disabled={submitted}
                  onChange={(e) => setEmployeePhone(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── Quotation Items ── */}
          <SectionTitle>Quotation Items</SectionTitle>
          <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
            <div className="grid grid-cols-[1fr_1.2fr_1.5fr_0.9fr_0.7fr_auto] gap-2 mb-2 pb-2 border-b border-slate-100">
              {["SKU", "Tally Sl. No.", "Item Description", "Amount (₹)", "GST %", ""].map((h, i) => (
                <span key={i} className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{h}</span>
              ))}
            </div>

            {items.map((row, i) => (
              <div key={i} className="grid grid-cols-[1fr_1.2fr_1.5fr_0.9fr_0.7fr_auto] gap-2 mb-2">
                <input className={INPUT_CLASS} placeholder="SKU"       disabled={submitted} value={row.sku}       onChange={(e) => updateItem(i, "sku",       e.target.value)} />
                <input className={INPUT_CLASS} placeholder="Sl. No"    disabled={submitted} value={row.tallySlNo} onChange={(e) => updateItem(i, "tallySlNo", e.target.value)} />
                <input className={INPUT_CLASS} placeholder="Item name" disabled={submitted} value={row.item}      onChange={(e) => updateItem(i, "item",      e.target.value)} />
                <input className={INPUT_CLASS} placeholder="0"  type="number" disabled={submitted} value={row.total}  onChange={(e) => updateItem(i, "total",  e.target.value)} />
                <input className={INPUT_CLASS} placeholder="18" type="number" disabled={submitted} value={row.gstPct} onChange={(e) => updateItem(i, "gstPct", e.target.value)} />
                {items.length > 1 && !submitted
                  ? <button type="button" onClick={() => removeItem(i)}
                      className="p-2 rounded-lg text-red-400 border border-red-100 hover:bg-red-50 transition-colors">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  : <div className="w-8" />
                }
              </div>
            ))}

            {!submitted && (
              <button type="button" onClick={addItem}
                className="mt-1 text-xs font-semibold text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors">
                + Add Item
              </button>
            )}
          </div>

          {/* ── Summary ── */}
          <SectionTitle>Summary</SectionTitle>
          <div className="rounded-xl bg-white border border-slate-200 overflow-hidden mb-4"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {["SKU", "Item", "Base (₹)", "GST %", "Incl. Tax (₹)"].map((h) => (
                    <th key={h} className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-600">{r.sku || "—"}</td>
                    <td className="px-4 py-2 text-slate-700">{r.item || "—"}</td>
                    <td className="px-4 py-2 text-slate-600">₹{fmt(r.base)}</td>
                    <td className="px-4 py-2 text-slate-500">{r.gstPct}%</td>
                    <td className="px-4 py-2 font-semibold text-blue-700">₹{fmt(r.inclTax)}</td>
                  </tr>
                ))}
                <tr className="bg-blue-50">
                  <td colSpan={4} className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Grand Total</td>
                  <td className="px-4 py-3 font-bold text-blue-800 text-base">₹{fmt(grandTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-slate-200 bg-white rounded-b-xl flex items-center gap-3 flex-wrap"
          style={{ boxShadow: "0 -1px 4px rgba(15,23,42,0.04)" }}>
          {submitMsg && (
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${
              submitMsg.includes("success") || submitMsg.includes("saved") || submitMsg.includes("emailed")
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-red-50 text-red-600 border border-red-200"
            }`}>
              {submitMsg}
            </span>
          )}

          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <button type="button" onClick={handleClose}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 bg-white transition-colors">
              Cancel
            </button>

            {!submitted && (
              <button type="button" onClick={handleSubmit}
                disabled={submitting || !isValid()}
                className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}>
                {submitting ? "Saving…" : "Submit Quotation"}
              </button>
            )}

            {submitted && (
              <>
                <button type="button" onClick={() => setShowPreview(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors">
                  <EyeIcon className="h-4 w-4" /> Preview
                </button>
                <button type="button" onClick={() => buildPDF(pdfParams(false))}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors">
                  <ArrowDownTrayIcon className="h-4 w-4" /> Download PDF
                </button>
                <button type="button" onClick={handleSendEmail} disabled={submitting}
                  className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40"
                  style={{ background: "linear-gradient(135deg, #0369a1, #0e7490)" }}>
                  {submitting ? "Sending…" : "Send to Email"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ══ Preview Overlay ══ */}
      {showPreview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ background: "rgba(15,23,42,0.72)", backdropFilter: "blur(6px)" }}>
          <div className="w-[680px] max-h-[88vh] rounded-xl flex flex-col bg-white border border-slate-200"
            style={{ boxShadow: "0 24px 80px rgba(15,23,42,0.28)" }}>

            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                <h3 className="text-base font-bold m-0 text-slate-800">Quotation Preview</h3>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => buildPDF(pdfParams(false))}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors">
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" /> Download
                </button>
                <button type="button" onClick={() => setShowPreview(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6" style={{ background: "#f0f4f8" }}>
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">

                {/* dark header */}
                <div className="px-6 py-4" style={{ background: "rgb(30,18,60)" }}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-bold text-sm m-0">SBE Fintech Software Pvt. Ltd.</p>
                      <p className="text-slate-400 text-xs mt-1 m-0">203/C, BB Ganguly Street, Kolkata – 700012</p>
                      <p className="text-slate-400 text-xs m-0">CIN: U62091WB2023PTC266642</p>
                    </div>
                    {/* logos from local paths */}
                    <div className="flex items-center gap-2">
                      <img src={LOGO1_PATH} alt="Logo 1" className="h-12 w-auto object-contain bg-white rounded p-0.5"
                        onError={(e) => { e.target.style.display = "none"; }} />
                      <img src={LOGO2_PATH} alt="Logo 2" className="h-12 w-auto object-contain bg-white rounded p-0.5"
                        onError={(e) => { e.target.style.display = "none"; }} />
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-xs text-slate-400 m-0">To.</p>
                      <p className="text-sm font-bold text-slate-800 m-0 mt-1">{effectiveCustomer?.companyName || "—"}</p>
                      <p className="text-xs text-slate-500 m-0">{effectiveCustomer?.address}</p>
                      {effectiveCustomer?.pincode && (
                        <p className="text-xs text-slate-500 m-0">Kolkata: {effectiveCustomer.pincode}</p>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      Date: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                    </p>
                  </div>

                  <p className="text-xs font-bold text-slate-700 border-b border-slate-200 pb-2 mb-3">
                    Sub: {subject || "—"}
                  </p>
                  <p className="text-xs text-slate-500 mb-4">
                    Dear Sir,<br />
                    SBE Fintech Software is pleased to submit the price proposal for the below items.
                    The pricing is quoted in INR only with a validity of 7 days from the date of offer.
                  </p>

                  <table className="w-full text-xs border border-slate-200 overflow-hidden mb-4 border-collapse">
                    <thead>
                      <tr style={{ background: "rgb(30,18,60)" }}>
                        {["SKU", "Item", "Total in INR", "Inclusive Tax"].map((h) => (
                          <th key={h} className="text-white px-3 py-2 text-center text-[10px] font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-3 py-1.5 text-slate-600">{r.sku || "—"}</td>
                          <td className="px-3 py-1.5 text-slate-700">{r.item || "—"}</td>
                          <td className="px-3 py-1.5 text-center text-slate-600">₹{fmt(r.base)}</td>
                          <td className="px-3 py-1.5 text-center text-slate-600">₹{fmt(r.inclTax)}</td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50">
                        <td colSpan={3} className="px-3 py-2 text-right text-xs font-bold text-slate-600">Total</td>
                        <td className="px-3 py-2 text-center font-bold text-blue-800">₹{fmt(grandTotal)}</td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="mb-4">
                    <p className="text-xs font-bold text-slate-700 mb-1">Terms and Condition:</p>
                    {[
                      "GST @ 18% will be applicable on all the prices mentioned above.",
                      "100% payment must be made in favour of SBE Fintech Software against the invoice raised.",
                      `This proposal is valid up to ${new Date(Date.now() + 7 * 864e5).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}.`,
                    ].map((t, i) => <p key={i} className="text-xs text-slate-500 m-0">• {t}</p>)}
                  </div>

                  <div className="mb-4">
                    <p className="text-xs font-bold text-slate-700 mb-1">Please Find The Account Details Below-</p>
                    {[
                      ["Account Number:", "50200087662987"],
                      ["IFSC:",           "HDFC0000008"],
                      ["Bank Name:",      "HDFC"],
                      ["Branch:",         "Stephen House"],
                      ["Account Type:",   "Current"],
                      ["From:",           "SBE Fintech Software Pvt Ltd."],
                    ].map(([k, v]) => (
                      <div key={k} className="flex gap-2 text-xs">
                        <span className="font-semibold text-slate-600 w-28">{k}</span>
                        <span className="text-slate-500">{v}</span>
                      </div>
                    ))}
                  </div>

                  {/* employee sign-off — dynamic */}
                  <div>
                    <p className="text-xs font-bold text-slate-700 m-0">{employeeName || "—"}</p>
                    {employeePhone && <p className="text-xs text-slate-500 m-0">{employeePhone}</p>}
                  </div>
                </div>

                <div className="px-6 py-3 border-t border-purple-200 bg-purple-50 text-center">
                  <p className="text-[10px] text-slate-500 m-0">Registered Office: 203/C, BB Ganguly Street, Kolkata – 700012 | CIN: U62091WB2023PTC266642</p>
                  <p className="text-[10px] text-slate-500 m-0">Branch: Ranchi / Kolkata / Dhaka | Mail: info@sbefintech.com | Toll Free: 1800 889 1973 | www.sbefintech.com</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}